import { apiFetch } from './client.js';

/**
 * Comments, on whatever they are about.
 *
 * One client for every subject, because a comment on a payment and a comment on
 * a client are the same act — somebody wrote something down and signed it.
 */

export type CommentSubject =
  | 'CUSTOMER'
  | 'APPLICATION'
  | 'INBOUND_PAYMENT'
  | 'PAYMENT'
  | 'WITHDRAWAL';

/** A client's whole history narrowed to one desk, or to the decisions (DEV-95). */
export type CommentTag = CommentSubject | 'DECISION';

export interface Comment {
  id: string;
  customerId: string;
  subjectType: string | null;
  subjectId: string | null;
  body: string;
  authorId: string;
  pinned: boolean;
  createdAt: string;
  editedBy: string | null;
  editedAt: string | null;
  /** How many times it has been corrected. Zero for most. */
  revisionCount: number;
  /** Who, in words. Falls back to the id only when the person is unknown. */
  authorName?: string | null;
  editorName?: string | null;
  /** When the author's correction window closes. */
  editableUntil?: string;
  /** DECISION: what was done to a transaction, with its reason; never edited here (DEV-95). */
  kind?: 'COMMENT' | 'DECISION';
  /** For a decision: what was done. */
  summary?: string;
}

export interface CommentPage {
  comments: Comment[];
  total: number;
  page: number;
  pageSize: number;
  /** Whole-client history only: how many entries each tag would show. */
  tagCounts?: Partial<Record<CommentTag, number>>;
}

export interface CommentRevision {
  body: string;
  authorId: string;
  replacedAt: string;
}

export const commentsApi = {
  list: (
    subjectType: CommentSubject,
    subjectId: string,
    page = 1,
    pageSize = 10,
    tag?: CommentTag,
  ): Promise<CommentPage> =>
    apiFetch(
      `/admin/comments?subjectType=${subjectType}&subjectId=${encodeURIComponent(subjectId)}` +
        `&page=${String(page)}&pageSize=${String(pageSize)}` +
        (tag === undefined ? '' : `&tag=${tag}`),
    ),

  add: (input: {
    customerId: string;
    subjectType: CommentSubject;
    subjectId: string;
    body: string;
  }): Promise<Comment> => apiFetch('/admin/comments', { method: 'POST', body: input }),

  /** Correcting keeps what it said before; the history is readable below it. */
  edit: (id: string, body: string): Promise<Comment> =>
    apiFetch(`/admin/comments/${id}`, { method: 'PATCH', body: { body } }),

  setPinned: (id: string, pinned: boolean): Promise<Comment> =>
    apiFetch(`/admin/comments/${id}/pin`, { method: 'POST', body: { pinned } }),

  revisions: (id: string): Promise<CommentRevision[]> =>
    apiFetch(`/admin/comments/${id}/revisions`),

  /** Take a note out of view. Administrators only; the record is kept. */
  remove: (id: string, reason: string): Promise<void> =>
    apiFetch(`/admin/comments/${id}`, { method: 'DELETE', body: { reason } }),
};
